import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Button, { ButtonType } from '../../../components/button/Button';
import Toolbar from '../../../components/toolbar/Toolbar';
import InputFieldText from '../../../components/input/floating-label/input-field/InputFieldText';
import Select from '../../../components/select/Select';
import { StateModel, StateModel_Benutzererfassung } from '../../../redux/store';
import { getSprachen } from '../../../redux/actions/sprache';
import { Benutzer, Benutzerversion, Sprache } from '../../../api';
import {
  getBenutzerById,
  resetBenutzererfassung,
  saveBenutzer,
  setBenutzerValue,
  updateBenutzer,
} from '../../../redux/actions/benutzererfassung';
import { utcIsoDateToLocaleDate } from '../../../utils/date';
import { checkUserExists } from '../../../redux/actions/benutzer';
import { toSelectOptions } from '../../../components/select/SelectModel';
import { nameof } from '../../../utils/objectUtils';
import SliderCheckbox from '../../../components/slidercheckbox/SliderCheckbox';
import { ConfigModel } from '../../../models/ConfigModel';

interface Benutzererfassungsprops {
  isAdmin: boolean;
  authorized: boolean | undefined;
  sprachen: Sprache[];
  getSprachen: () => void;
  updateBenutzer: (
    benutzerId: number,
    benutzer: Benutzer,
    closeFunction: () => void
  ) => void;
  saveBenutzer: (benutzer: Benutzer, closeFunction?: () => void) => void;
  benutzererfassung: StateModel_Benutzererfassung;
  setBenutzerValue: (
    value: string | number | undefined | boolean,
    fieldName: string
  ) => void;
  getBenutzerById: (benutzerId: number) => void;
  resetBenutzererfassung: () => void;
  close: () => void;
  checkUserExists: (name: string) => void;
  config: ConfigModel;
  canEdit: boolean;
  isMetro: boolean;
  rowsInGrid: number;
}

export const benutzerVersionen = (
  isMetro?: boolean
): Array<Benutzerversion> => {
  const versionen = [];

  if (isMetro) {
    versionen.push({ id: 2, name: 'Metroversion' });
  } else {
    versionen.push({ id: 1, name: 'Standardversion' });
  }
  return versionen;
};
/**
 * Benutzererfassung-Component
 *
 * @returns {JSX.Element} Benutzererfassung-Component
 * @constructor
 */
const Benutzererfassung = ({
  isAdmin,
  sprachen,
  getSprachen,
  updateBenutzer,
  saveBenutzer,
  benutzererfassung,
  setBenutzerValue,
  getBenutzerById,
  resetBenutzererfassung,
  checkUserExists,
  close,
  config,
  canEdit, //Entspricht der Spalte 'edit' in der Tabelle users
  isMetro, // Benutzer erhält immer die Standardversion, es sei denn, er ist im D+ als Metrokunde (BEXity) gekennzeichnet, dann erhlät er die Metroversion.
  rowsInGrid,
}: // diese ist nicht änderbar!
Benutzererfassungsprops) => {
  const [username, setUsername] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [passwordRepeat, setPasswordRepeat] = useState<string>();
  const [spracheId, setSpracheId] = useState<number>();
  const [versionId, setVersionId] = useState<number | undefined>(0);
  const [authorized, setAuthorized] = useState<boolean>();
  const [rowsingrid, setRowsInGrid] = useState(+rowsInGrid);
  const isBexity = config.clientKunde?.toUpperCase() === 'BEXITY';
  const minLenghtPW = !config.clientMinLengthPw
    ? isBexity
      ? 12
      : 8
    : config.clientMinLengthPw;
  const pwHint =
    'Bitte mindestens ' +
    minLenghtPW +
    ', max. 25 Zeichen, einen Großbuchstaben, eine Zahl und ein Sonderzeichen eingeben';
  const pwLabel =
    'Benutzer Passwort (min. ' +
    minLenghtPW +
    ', max. 25 Zeichen, eine Zahl, einen Großbuchstaben und ein Sonderzeichen)';

  useEffect(() => {
    getSprachen();
    if (!!benutzererfassung.id) {
      getBenutzerById(benutzererfassung.id);
    }
  }, []);

  useEffect(() => {
    //#84067 Passwort prüfen
    const html_password = document.getElementById(
      'userpassword'
    ) as HTMLObjectElement;
    if (!checkPassword(password)) {
      html_password?.setCustomValidity(pwHint);
    } else {
      html_password?.setCustomValidity('');
    }
  }, [password]);

  const checkPassword = (value: string | undefined) => {
    //#84067 Passwort mindestanforderung , wird ausgeführt wenn min. ein zeichen einegegben wurd
    if (value != null && value.length > 0) {
      if (!/[0-9]/.test(value)) return false; //Zahl?
      if (value.toLowerCase() == value) return false; //Großbuchstabe?
      if (!/[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value)) return false; //Sonderzeichen?
      if (value.length < minLenghtPW || value.length > 25) return false; //LängeOK?
      return true;
    }
    return true; //Wenn PW leer sollten wohl andere Daten geändert werden
  };

  const buildBenutzerPayload = (): Benutzer => {
    const benutzer: Benutzer = {
      id: benutzererfassung?.id,
      name: benutzererfassung?.name?.trim(),
      email: benutzererfassung?.email,
      benutzerversion: {
        id: Number(benutzererfassung.benutzerversion?.id),
      },
      sprache: {
        id: Number(benutzererfassung?.sprache?.id),
      },
      password: benutzererfassung?.password,
      hasAuthorized: benutzererfassung?.authorized,

      main: benutzererfassung?.main,
      admin: benutzererfassung?.admin,
      invisibleEinmaladresse: benutzererfassung?.invisibleEinmaladresse,
      edit:
        config.isUserCanEditImmediately && canEdit
          ? true
          : benutzererfassung?.edit,
      rowsInGrid: benutzererfassung?.rowsInGrid,
    };
    return benutzer;
  };

  useEffect(() => {
    setBenutzerValue(username, nameof<StateModel_Benutzererfassung>('name'));
    username && checkUserExists(username);
  }, [username]);

  useEffect(() => {
    const htmlUsername = document.getElementById(
      'username'
    ) as HTMLObjectElement;
    if (benutzererfassung.usernameExists) {
      htmlUsername?.setCustomValidity('Der Username ist schon vorhanden.');
    } else {
      htmlUsername?.setCustomValidity('');
    }
  }, [benutzererfassung.usernameExists]);

  useEffect(() => {
    setBenutzerValue(
      password,
      nameof<StateModel_Benutzererfassung>('password')
    );
  }, [password]);

  useEffect(() => {
    setBenutzerValue(
      passwordRepeat,
      nameof<StateModel_Benutzererfassung>('passwordRepeat')
    );
  }, [passwordRepeat]);

  useEffect(() => {
    setBenutzerValue(email, nameof<StateModel_Benutzererfassung>('email'));
  }, [email]);

  useEffect(() => {
    setBenutzerValue(
      Number(spracheId),
      nameof<StateModel_Benutzererfassung>('sprache')
    );
  }, [spracheId]);

  useEffect(() => {
    setBenutzerValue(
      versionId,
      nameof<StateModel_Benutzererfassung>('benutzerversion')
    );
  }, [versionId]);

  useEffect(() => {
    setBenutzerValue(
      authorized,
      nameof<StateModel_Benutzererfassung>('authorized')
    );
  }, [authorized]);

  useEffect(() => {
    setBenutzerValue(
      rowsingrid,
      nameof<StateModel_Benutzererfassung>('rowsInGrid')
    );
  }, [rowsingrid]);

  useEffect(() => {
    const htmlConfirm_password = document.getElementById(
      'userpasswordrepeat'
    ) as HTMLObjectElement;

    if (password !== passwordRepeat) {
      htmlConfirm_password?.setCustomValidity(
        'Die Passwörter sind nicht identisch.'
      );
    } else {
      htmlConfirm_password?.setCustomValidity('');
    }
  }, [password, passwordRepeat]);

  const abbrechen = () => {
    close();
  };

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!!benutzererfassung.id) {
      updateBenutzer(benutzererfassung.id, buildBenutzerPayload(), close);
    } else {
      saveBenutzer(buildBenutzerPayload(), close);
    }
  };

  return (
    <div className={'wrapper-benutzererfassung'}>
      <form onSubmit={submit}>
        <Toolbar>
          <Button
            tooltip={'Speichern'}
            key={0}
            classes={'iconButton'}
            type={ButtonType.submit}
          >
            <i className='far fa-save fa-2x' />
          </Button>
          <Button
            tooltip='Abbrechen'
            classes={'iconButton'}
            key={1}
            onClickHandler={() => abbrechen()}
          >
            <i className='fas fa-times fa-2x color-red' />
          </Button>
          {isAdmin && (
            <div>
              <label>Aktiv: </label>
              <SliderCheckbox
                value={benutzererfassung.authorized ?? false}
                onChangeHandler={setAuthorized}
                labelActive={<i className='fas fa-user-check' />}
                labelInactive={<i className='fas fa-user-alt-slash' />}
                disabled={false}
              />
            </div>
          )}
        </Toolbar>
        {benutzererfassung.id && benutzererfassung.created && (
          <div id='angelegt-userId' className={'marginContainer'}>
            {benutzererfassung.id && (
              <p className='userId'>Benutzer ID: {benutzererfassung.id}</p>
            )}
            {benutzererfassung.created && (
              <p className='marginItem'>
                angelegt am:{' '}
                {utcIsoDateToLocaleDate(benutzererfassung.created ?? '')}
              </p>
            )}
          </div>
        )}
        <div id='username-email'>
          <InputFieldText
            id={'username'}
            label='Benutzername'
            name='username'
            classes={'username' + benutzererfassung.id && ' noSelect'}
            onChangeHandler={setUsername}
            value={benutzererfassung.name}
            required
            readonly={!!benutzererfassung.id}
          />
          <InputFieldText
            label='E-Mail'
            name='email'
            classes='email'
            onChangeHandler={setEmail}
            value={benutzererfassung.email}
            inputType={'email'}
          />
        </div>
        <div id='password-passwordrepeat'>
          <InputFieldText
            id={'userpassword'}
            label='Benutzer Passwort'
            name='userpassword'
            classes='userpassword'
            onChangeHandler={setPassword}
            value={benutzererfassung.password}
            inputType={'password'}
            required={!benutzererfassung.id}
            tooltip={pwLabel}
          />
          <InputFieldText
            id={'userpasswordrepeat'}
            label='Benutzer Passwort bestätigen'
            name='userpasswordrepeat'
            classes='userpasswordrepeat'
            onChangeHandler={setPasswordRepeat}
            value={benutzererfassung.passwordRepeat}
            inputType={'password'}
            required={!benutzererfassung.id}
          />
        </div>
        <div>
          <Select
            className='sprache'
            headerTitle='Sprache'
            selectedOptionId={benutzererfassung.sprache?.id}
            onChangeHandler={setSpracheId}
            selectData={toSelectOptions(
              sprachen,
              nameof<Sprache>('bezeichnung'),
              false
            )}
          />
        </div>
        <div>
          <Select
            className='version'
            headerTitle='Version'
            selectedOptionId={benutzererfassung.benutzerversion?.id}
            //onChangeHandler={setVersionId}
            selectData={toSelectOptions(
              benutzerVersionen(isMetro),
              nameof<Benutzerversion>('name'),
              false
            )}
            readonly={true}
          />
        </div>
        <div>
          <Select
            className='rowsInGrid'
            headerTitle='Anzahl Zeilen in Listen'
            selectedOptionId={rowsingrid}
            onChangeHandler={value => setRowsInGrid(value ?? 13)}
            selectData={toSelectOptions(
              [
                { id: 13, label: '13' },
                { id: 25, label: '25' },
                { id: 50, label: '50' },
                { id: 100, label: '100' },
              ],
              'label',
              false
            )}
          />
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: StateModel) => ({
  isAdmin: state.UserSession.isAdmin,
  canEdit: state.UserSession.isAuftragEdit,
  isMetro: state.UserSession.isMetro,
  sprachen: state.Stammdaten.sprachen,
  benutzererfassung: state.Benutzererfassung,
  authorized: state.Benutzererfassung.authorized,
  config: state.Config,
  rowsInGrid: state.UserSession.rowsInGrid,
});

export default connect(mapStateToProps, {
  getSprachen,
  saveBenutzer,
  updateBenutzer,
  setBenutzerValue,
  getBenutzerById,
  resetBenutzererfassung,
  checkUserExists,
})(Benutzererfassung);
